import { httpsCallable } from "firebase/functions";
import * as Sentry from "@sentry/react";
import {
  callableUrl,
  installCallableFetchObserver,
  isOpaqueInternalError,
  reportCallableInternalFailure,
  watchCallableUrl,
} from "./callableDiagnostics";

/**
 * Drop-in replacement for httpsCallable that adds Sentry distributed tracing.
 * Passes trace context via the data payload so the backend withSentry wrapper
 * can continue the trace.
 *
 * Usage:
 *   // Before: const myFunc = httpsCallable(functions, "myFunc");
 *   // After:  const myFunc = tracedCallable(functions, "myFunc");
 *   const result = await myFunc({ orgId: "abc" });
 *
 * Accepts the same options as httpsCallable (e.g. { timeout: 20000 }).
 *
 * It is also the app's single choke point for callable failures — every call
 * site but a handful funnels through here — so GH#566's instrumentation hangs
 * off this catch rather than off each caller. The rejection is re-thrown
 * untouched; callers see exactly the error they saw before.
 */
export function tracedCallable(functions, name, options) {
  // options passes through to httpsCallable — e.g. { timeout: 240000 } for
  // long-running calls (the SDK default of 70s is shorter than a large seed).
  const callable = httpsCallable(functions, name, options);

  // Register the URL the SDK will POST to before it ever posts, so the fetch
  // observer can recognise the request when it goes out.
  const url = callableUrl(functions, name);
  watchCallableUrl(url);
  installCallableFetchObserver();

  return (data) =>
    Sentry.startSpan(
      { name: `callable/${name}`, op: "http.client" },
      async (span) => {
        const traceHeaders = span
          ? {
              _sentryTrace: Sentry.spanToTraceHeader(span),
              _sentryBaggage: Sentry.spanToBaggageHeader(span),
            }
          : {};

        const startedAt = Date.now();
        try {
          return await callable({ ...data, ...traceHeaders });
        } catch (error) {
          try {
            // Only the SDK's opaque transport failure. A real server-side
            // HttpsError is already attributable from the function's own logs
            // and must not pay for a probe.
            if (isOpaqueInternalError(error)) {
              await reportCallableInternalFailure({
                functionsInstance: functions,
                name,
                data,
                url,
                error,
                startedAt,
              });
            }
          } catch {
            // The caller's error is the one that matters. Nothing the
            // instrumentation does may replace it.
          }
          throw error;
        }
      },
    );
}
